// File: nodes/DTMFNode.jsx
import React from 'react';
import { Handle, Position } from 'reactflow';
import {
  Box,
  Flex,
  Text,
  IconButton,
  HStack,
  VStack,
} from '@chakra-ui/react';
import {
  FaHashtag,
  FaClock,
  FaPhoneSlash
} from 'react-icons/fa';
import { FiCopy, FiEdit, FiTrash2 } from 'react-icons/fi';

const DTMFNode = ({ data }) => {
  const dtmfResponses = data.dtmfResponses || [];
  const activeDTMF = dtmfResponses.filter(
    (resp) => resp.key && resp.key.trim() !== ''
  );

  const getDTMFColor = (index) => {
    const colors = [
      {
        bg: 'var(--brand-primary)',
        light: 'rgba(1, 172, 251, 0.1)',
        border: 'rgba(1, 172, 251, 0.3)',
      },
      {
        bg: '#9333EA',
        light: 'rgba(147, 51, 234, 0.1)',
        border: 'rgba(147, 51, 234, 0.3)',
      },
      {
        bg: '#10B981',
        light: 'rgba(16, 185, 129, 0.1)',
        border: 'rgba(16, 185, 129, 0.3)',
      },
      {
        bg: '#F59E0B',
        light: 'rgba(245, 158, 11, 0.1)',
        border: 'rgba(245, 158, 11, 0.3)',
      },
    ];
    return colors[index] || colors[0];
  };

  return (
    <Box
      bg="white"
      border="2px solid"
      borderColor="#FBAF3F"
      borderRadius="xl"
      boxShadow={data.selected ? 'xl' : 'lg'}
      minW="240px"
      position="relative"
    >
      <Handle
        type="target"
        position={Position.Left}
       style={{ background: 'black' }}
      />

      {/* Header */}
      <Flex
        bg="#FBAF3F"
        color="white"
        px={4}
        py={2.5}
        borderTopRadius="xl"
        align="center"
        gap={2}
      >
        <FaHashtag size={16} />
        <Text noOfLines={1} flex={1} fontSize="sm" fontWeight="medium">
          DTMF Response
        </Text>
        <HStack spacing={1}>
          <IconButton
            icon={<FiEdit size={14} />}
            size="xs"
            variant="ghost"
            color="white"
            _hover={{ bg: 'whiteAlpha.300' }}
            onClick={(e) => {
              e.stopPropagation();
              data.onEdit?.(data.id);
            }}
            aria-label="Edit"
          />
          <IconButton
            icon={<FiCopy size={14} />}
            size="xs"
            variant="ghost"
            color="white"
            _hover={{ bg: 'whiteAlpha.300' }}
            onClick={(e) => {
              e.stopPropagation();
              data.onDuplicate?.(data.id);
            }}
            aria-label="Duplicate"
          />
          <IconButton
            icon={<FiTrash2 size={14} />}
            size="xs"
            variant="ghost"
            color="white"
            _hover={{ bg: 'whiteAlpha.300' }}
            onClick={(e) => {
              e.stopPropagation();
              data.onDelete?.(data.id);
            }}
            aria-label="Delete"
          />
        </HStack>
      </Flex>

      {/* Body */}
      <Box p={3}>
        <Text fontSize="sm" fontWeight="medium" mb={3}>
          {data.label || 'DTMF Response'}
        </Text>

        <Text fontSize="xs" color="gray.600" mb={3}>
          {activeDTMF.length} response{activeDTMF.length !== 1 ? 's' : ''}{' '}
          configured
        </Text>

        <VStack spacing={3} align="stretch">
          {activeDTMF.map((response, index) => {
            const color = getDTMFColor(index);

            return (
              <Box
                key={response.id || index}
                border="1px solid"
                borderColor={color.border}
                borderRadius="lg"
                bg={color.light}
                p={2}
                position="relative"
              >
                <Flex align="center" gap={2} mb={2}>
                  <Flex
                    w={6}
                    h={6}
                    borderRadius="md"
                    bg={color.bg}
                    border="2px solid white"
                    align="center"
                    justify="center"
                    flexShrink={0}
                  >
                    <Text color="white" fontSize="10px" fontWeight="bold">
                      {response.key}
                    </Text>
                  </Flex>
                  <Text
                    fontSize="xs"
                    fontWeight="medium"
                    flex={1}
                    noOfLines={1}
                  >
                    {response.label}
                  </Text>
                  <Box color={color.bg}>
                    <FaHashtag size={14} />
                  </Box>
                </Flex>

                {response.timeoutEnabled && (
                  <Flex
                    align="center"
                    gap={1}
                    fontSize="10px"
                    color="orange.700"
                    bg="orange.50"
                    px={1.5}
                    py={1}
                    borderRadius="md"
                    mb={2}
                  >
                    <FaClock size={10} />
                    <Text>
                      Wait {response.timeoutDuration} {response.timeoutUnit}
                    </Text>
                  </Flex>
                )}

                {/* Handle positioned on the right side of each response row */}
                <Handle
                  type="source"
                  position={Position.Right}
                  id={`${response.id}_received`}
                  className="branch-handle"
                  style={{
                    width: 8,
                    height: 8,
                    background: color.bg,
                    border: '1px solid white',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    '--flow-branch-offset': '-19px',
                  }}
                />
              </Box>
            );
          })}

          {/* No Response / Fallback section */}
          <Box
            borderTop="1px solid"
            borderColor="gray.200"
            pt={3}
          >
            <Box
              border="1px solid"
              borderColor="gray.200"
              borderRadius="lg"
              bg="gray.50"
              p={2}
              position="relative"
            >
              <Flex align="center" gap={2} mb={data.noResponseWaitEnabled ? 2 : 0}>
                <Box color="gray.500">
                  <FaPhoneSlash size={14} />
                </Box>
                <Text fontSize="xs" fontWeight="medium" color="gray.700">
                  No Response / Fallback
                </Text>
              </Flex>

              {data.noResponseWaitEnabled && (
                <Flex
                  align="center"
                  gap={1}
                  fontSize="10px"
                  color="orange.700"
                  bg="orange.50"
                  px={1.5}
                  py={1}
                  borderRadius="md"
                  mb={2}
                >
                  <FaClock size={10} />
                  <Text>
                    If no response in {data.noResponseWaitTime}{' '}
                    {data.noResponseWaitUnit}
                  </Text>
                </Flex>
              )}

              <Flex align="center" gap={2} position="relative">
                <Box w={1.5} h={1.5} borderRadius="full" bg="gray.400" />
                <Text fontSize="10px" color="gray.600" fontWeight="medium">
                  No Response
                </Text>
                <Handle
                  type="source"
                  position={Position.Right}
                  id="no_response"
                  className="branch-handle"
                  style={{
                    width: 8,
                    height: 8,
                    background: '#9CA3AF',
                    border: '1px solid white',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    '--flow-branch-offset': '-27px',
                  }}
                />
              </Flex>
            </Box>
          </Box>
        </VStack>
      </Box>
    </Box>
  );
};

export default DTMFNode;